/**
 * ComposeEmailDialog
 * Two-step compose (BR-116/BR-117): pick recipients — tenants (unit, then tenant) and/or vendors
 * (search + multi-select) in one persistent tray, with a "Send to" choice for a counterparty that
 * has more than one recognized address — then write the message. One send creates one thread with
 * a separate copy per recipient.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  listUnits,
  listTenantsByUnit,
  listCounterpartyAddresses,
  sendEmail,
  type UnitWithRelations,
  type TenantWithDetails,
  type CounterpartyKind,
} from '../../lib/api';
import { listVendors, type Vendor } from '../../api/vendors.api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { SegmentedRadioGroup } from './SegmentedRadioGroup';
import { unitOptionLabel, tenantDisplayName, isReachableTenant } from './email-labels';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

type ComposeStep = 'recipients' | 'message';
type RecipientType = 'tenants' | 'vendors';

const RECIPIENT_TYPE_OPTIONS = [
  { value: 'tenants', label: 'Tenants', testId: 'recipient-type-tenants' },
  { value: 'vendors', label: 'Vendors', testId: 'recipient-type-vendors' },
] as const;

const NO_EMAIL_REASON = "No email on file. Add one on the vendor's page to write to them.";

interface ComposeRecipient {
  kind: CounterpartyKind;
  id: string;
  name: string;
  /** Unit label for a tenant, trades for a vendor. */
  subtitle: string | null;
  primary: string | null;
  /** Extra recognized addresses beyond the primary. */
  extraAddresses: string[];
  /** The address this send goes to; the primary unless the landlord picked another. */
  address: string | null;
}

type RecipientCandidate = Pick<ComposeRecipient, 'kind' | 'id' | 'name' | 'subtitle' | 'primary'>;

const keyOf = (r: Pick<ComposeRecipient, 'kind' | 'id'>): string => `${r.kind}:${r.id}`;

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** "2 recipients: 1 tenant, 1 vendor" — a zero part is omitted. */
function trayCount(recipients: ComposeRecipient[]): string {
  const tenants = recipients.filter((r) => r.kind === 'tenant').length;
  const vendors = recipients.length - tenants;
  const parts = [
    tenants > 0 ? pluralize(tenants, 'tenant') : null,
    vendors > 0 ? pluralize(vendors, 'vendor') : null,
  ].filter(Boolean);
  return `${pluralize(recipients.length, 'recipient')}: ${parts.join(', ')}`;
}

interface ComposeEmailDialogProps {
  onClose: () => void;
  onEmailSent: () => void;
}

export function ComposeEmailDialog({ onClose, onEmailSent }: ComposeEmailDialogProps): JSX.Element {
  const [step, setStep] = useState<ComposeStep>('recipients');
  const [recipientType, setRecipientType] = useState<RecipientType>('tenants');
  const [recipients, setRecipients] = useState<ComposeRecipient[]>([]);
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape closes; Tab is trapped inside the dialog.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialogRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Move focus into the dialog on open and restore it to the trigger on close.
  useEffect(() => {
    const trigger = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (trigger instanceof HTMLElement) trigger.focus();
    };
  }, []);

  const removeRecipient = useCallback((key: string) => {
    setRecipients((prev) => prev.filter((r) => keyOf(r) !== key));
  }, []);

  // Mirrors `recipients` so a toggle can tell add from remove without reading inside a state updater.
  const recipientsRef = useRef(recipients);
  recipientsRef.current = recipients;

  const toggleRecipient = useCallback((candidate: RecipientCandidate) => {
    const key = keyOf(candidate);
    if (recipientsRef.current.some((r) => keyOf(r) === key)) {
      setRecipients((prev) => prev.filter((r) => keyOf(r) !== key));
      return;
    }
    setRecipients((prev) => [...prev, { ...candidate, extraAddresses: [], address: candidate.primary }]);
    // Learn the recognized addresses so a "Send to" choice can be offered (BR-117). A failed
    // lookup just leaves the primary as the only address.
    listCounterpartyAddresses(candidate.kind, candidate.id)
      .then(({ primary, addresses }) => {
        setRecipients((prev) =>
          prev.map((r) =>
            keyOf(r) === key
              ? {
                  ...r,
                  primary: primary ?? r.primary,
                  address: primary ?? r.address,
                  extraAddresses: addresses.map((a) => a.address),
                }
              : r
          )
        );
      })
      .catch(() => undefined);
  }, []);

  const chooseAddress = useCallback((key: string, address: string) => {
    setRecipients((prev) => prev.map((r) => (keyOf(r) === key ? { ...r, address } : r)));
  }, []);

  const handleSend = useCallback(async () => {
    if (recipients.length === 0 || !bodyText.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setIsSending(true);
      setError(null);
      await sendEmail({
        recipients: recipients.map((r) => ({
          kind: r.kind,
          id: r.id,
          ...(r.address && r.address !== r.primary && { address: r.address }),
        })),
        subject: subject.trim() || '(No subject)',
        bodyText: bodyText.trim(),
      });
      onEmailSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  }, [recipients, subject, bodyText, onEmailSent]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[1000]"
      onClick={onClose}
      data-testid="compose-email-dialog"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="compose-dialog-title"
        className="bg-card border border-border w-full md:w-[90%] md:max-w-[700px] h-[94%] md:h-auto md:max-h-[80vh] rounded-t-2xl md:rounded-lg overflow-auto p-4 focus:outline-none md:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="compose-dialog-title" className="m-0 text-lg md:text-xl font-semibold text-foreground">
            {step === 'recipients' ? 'Select Recipients' : 'Compose Email'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
            data-testid="close-compose"
            className="text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px]"
          >
            &times;
          </Button>
        </div>

        {error && (
          <div
            role="alert"
            className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
            data-testid="compose-error"
          >
            {error}
          </div>
        )}

        {step === 'recipients' ? (
          <RecipientsStep
            recipientType={recipientType}
            onRecipientTypeChange={setRecipientType}
            recipients={recipients}
            onToggle={toggleRecipient}
            onRemove={removeRecipient}
            onChooseAddress={chooseAddress}
            onCancel={onClose}
            onContinue={() => {
              setError(null);
              setStep('message');
            }}
            onError={setError}
          />
        ) : (
          <MessageStep
            recipients={recipients}
            subject={subject}
            bodyText={bodyText}
            isSending={isSending}
            onSubjectChange={setSubject}
            onBodyChange={setBodyText}
            onEdit={() => setStep('recipients')}
            onSend={() => void handleSend()}
          />
        )}
      </div>
    </div>
  );
}

function RecipientTray({
  recipients,
  onRemove,
}: {
  recipients: ComposeRecipient[];
  onRemove: (key: string) => void;
}): JSX.Element {
  return (
    <div
      aria-live="polite"
      data-testid="recipient-tray"
      className="p-3 mb-4 rounded-md border border-border bg-muted/50"
    >
      <div className="text-xs font-semibold text-muted-foreground" data-testid="recipient-tray-count">
        {recipients.length === 0 ? 'No recipients selected' : trayCount(recipients)}
      </div>
      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {recipients.map((r) => (
            <span
              key={keyOf(r)}
              data-testid={`recipient-pill-${r.id}`}
              className="inline-flex items-center gap-1 pl-3 rounded-full border border-border bg-card text-sm text-foreground"
            >
              {r.name}
              {r.subtitle ? ` · ${r.subtitle}` : ''}
              <button
                type="button"
                aria-label={`Remove ${r.name}`}
                data-testid={`remove-recipient-${r.id}`}
                onClick={() => onRemove(keyOf(r))}
                className="min-h-[44px] min-w-[44px] text-muted-foreground hover:text-foreground"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SendToSelect({
  recipient,
  onChoose,
}: {
  recipient: ComposeRecipient;
  onChoose: (key: string, address: string) => void;
}): JSX.Element | null {
  if (recipient.extraAddresses.length === 0 || !recipient.primary) return null;
  const selectId = `send-to-${recipient.kind}-${recipient.id}`;
  return (
    <div className="flex items-center gap-2.5 pl-11 mb-2">
      <label htmlFor={selectId} className="text-sm font-semibold text-foreground whitespace-nowrap">
        Send to
      </label>
      <select
        id={selectId}
        data-testid={`send-to-${recipient.id}`}
        value={recipient.address ?? recipient.primary}
        onChange={(e) => onChoose(keyOf(recipient), e.target.value)}
        className="flex-1 min-w-0 px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground min-h-[44px]"
      >
        <option value={recipient.primary}>{recipient.primary} (primary)</option>
        {recipient.extraAddresses.map((address) => (
          <option key={address} value={address}>
            {address}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckRow({
  selected,
  disabled,
  onClick,
  testId,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (!disabled) onClick();
      }}
      data-testid={testId}
      className={`w-full p-4 mb-2 border rounded-md text-left flex items-center gap-3 min-h-[44px] transition-colors ${
        disabled
          ? 'border-border bg-muted/50 opacity-70 cursor-not-allowed'
          : selected
            ? 'border-primary bg-primary/5 cursor-pointer'
            : 'border-border bg-card hover:bg-accent cursor-pointer'
      }`}
    >
      <div
        aria-hidden="true"
        className={`w-[18px] h-[18px] border-2 rounded flex items-center justify-center shrink-0 text-xs ${
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'
        }`}
      >
        {selected ? '✓' : ''}
      </div>
      <div className="min-w-0">{children}</div>
    </button>
  );
}

interface RecipientsStepProps {
  recipientType: RecipientType;
  onRecipientTypeChange: (type: RecipientType) => void;
  recipients: ComposeRecipient[];
  onToggle: (candidate: RecipientCandidate) => void;
  onRemove: (key: string) => void;
  onChooseAddress: (key: string, address: string) => void;
  onCancel: () => void;
  onContinue: () => void;
  onError: (message: string | null) => void;
}

function RecipientsStep({
  recipientType,
  onRecipientTypeChange,
  recipients,
  onToggle,
  onRemove,
  onChooseAddress,
  onCancel,
  onContinue,
  onError,
}: RecipientsStepProps): JSX.Element {
  // Tenants: the unit list, then that unit's tenants. Back returns to the unit list.
  const [selectedUnit, setSelectedUnit] = useState<UnitWithRelations | null>(null);
  const selectedKeys = new Set(recipients.map(keyOf));
  const recipientByKey = (kind: CounterpartyKind, id: string): ComposeRecipient | undefined =>
    recipients.find((r) => r.kind === kind && r.id === id);

  return (
    <>
      <RecipientTray recipients={recipients} onRemove={onRemove} />

      <SegmentedRadioGroup
        ariaLabel="Recipient type"
        options={[...RECIPIENT_TYPE_OPTIONS]}
        value={recipientType}
        onChange={onRecipientTypeChange}
        testId="recipient-type-group"
        className="mb-4"
      />

      {/* Both pickers stay mounted so switching type keeps the chosen unit and loaded lists. */}
      <div className={recipientType === 'tenants' ? '' : 'hidden'}>
        <TenantRecipients
          selectedUnit={selectedUnit}
          onSelectUnit={setSelectedUnit}
          selectedKeys={selectedKeys}
          recipientByKey={recipientByKey}
          onToggle={onToggle}
          onChooseAddress={onChooseAddress}
          onError={onError}
        />
      </div>
      <div className={recipientType === 'vendors' ? '' : 'hidden'}>
        <VendorRecipients
          selectedKeys={selectedKeys}
          recipientByKey={recipientByKey}
          onToggle={onToggle}
          onChooseAddress={onChooseAddress}
          onError={onError}
        />
      </div>

      <div className="flex gap-3 justify-end mt-4">
        {recipientType === 'tenants' && selectedUnit ? (
          <Button
            variant="outline"
            onClick={() => setSelectedUnit(null)}
            data-testid="compose-back-button"
          >
            Back
          </Button>
        ) : (
          <Button variant="outline" onClick={onCancel} data-testid="compose-cancel-button">
            Cancel
          </Button>
        )}
        <Button onClick={onContinue} disabled={recipients.length === 0} data-testid="continue-button">
          Continue
        </Button>
      </div>
    </>
  );
}

interface RecipientListProps {
  selectedKeys: Set<string>;
  recipientByKey: (kind: CounterpartyKind, id: string) => ComposeRecipient | undefined;
  onToggle: (candidate: RecipientCandidate) => void;
  onChooseAddress: (key: string, address: string) => void;
  onError: (message: string | null) => void;
}

function TenantRecipients({
  selectedUnit,
  onSelectUnit,
  selectedKeys,
  recipientByKey,
  onToggle,
  onChooseAddress,
  onError,
}: RecipientListProps & {
  selectedUnit: UnitWithRelations | null;
  onSelectUnit: (unit: UnitWithRelations | null) => void;
}): JSX.Element {
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [tenants, setTenants] = useState<TenantWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listUnits()
      .then(({ units: loaded }) => {
        if (!cancelled) setUnits(loaded);
      })
      .catch((err) => onError(err instanceof Error ? err.message : 'Failed to load units'))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [onError]);

  const handleUnitSelect = useCallback(
    async (unit: UnitWithRelations) => {
      try {
        setIsLoading(true);
        onError(null);
        const { tenants: loaded } = await listTenantsByUnit(unit.id);
        setTenants(loaded.filter(isReachableTenant));
        onSelectUnit(unit);
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Failed to load tenants');
      } finally {
        setIsLoading(false);
      }
    },
    [onError, onSelectUnit]
  );

  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (!selectedUnit) {
    return (
      <div className="max-h-[400px] overflow-auto">
        {units.length === 0 ? (
          <p className="text-center text-muted-foreground">No units found</p>
        ) : (
          units.map((unit) => (
            <button
              key={unit.id}
              type="button"
              onClick={() => void handleUnitSelect(unit)}
              className="w-full p-4 mb-2 border border-border rounded-md bg-card text-left cursor-pointer hover:bg-accent min-h-[44px]"
              data-testid={`unit-option-${unit.id}`}
            >
              <div className="font-semibold mb-1 text-foreground">{unitOptionLabel(unit)}</div>
              <div className="text-sm text-muted-foreground">
                {unit.address1}, {unit.city}, {unit.state}
              </div>
            </button>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-auto" role="group" aria-label="Tenants">
      {tenants.length === 0 ? (
        <p className="text-center text-muted-foreground">No tenants in this unit</p>
      ) : (
        tenants.map((tenant) => {
          const selected = selectedKeys.has(`tenant:${tenant.id}`);
          const recipient = recipientByKey('tenant', tenant.id);
          return (
            <div key={tenant.id}>
              <CheckRow
                selected={selected}
                testId={`tenant-option-${tenant.id}`}
                onClick={() =>
                  onToggle({
                    kind: 'tenant',
                    id: tenant.id,
                    name: tenantDisplayName(tenant),
                    subtitle: unitOptionLabel(selectedUnit),
                    primary: tenant.email || null,
                  })
                }
              >
                <div className="font-semibold text-foreground">{tenantDisplayName(tenant)}</div>
                {tenant.email && <div className="text-sm text-muted-foreground">{tenant.email}</div>}
              </CheckRow>
              {recipient && <SendToSelect recipient={recipient} onChoose={onChooseAddress} />}
            </div>
          );
        })
      )}
    </div>
  );
}

function VendorRecipients({
  selectedKeys,
  recipientByKey,
  onToggle,
  onChooseAddress,
  onError,
}: RecipientListProps): JSX.Element {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    listVendors()
      .then(({ vendors: loaded }) => {
        if (!cancelled) setVendors(loaded.filter((v) => v.isActive));
      })
      .catch((err) => onError(err instanceof Error ? err.message : 'Failed to load vendors'))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [onError]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter(
      (v) => v.name.toLowerCase().includes(q) || v.trades.some((t) => t.toLowerCase().includes(q))
    );
  }, [vendors, search]);

  return (
    <>
      <div className="mb-3">
        <label htmlFor="compose-vendor-search" className="block text-sm font-medium text-foreground mb-1.5">
          Search vendors
        </label>
        <input
          id="compose-vendor-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name or trade"
          data-testid="compose-vendor-search"
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground min-h-[44px]"
        />
      </div>
      {isLoading ? (
        <div className="space-y-2 py-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <div className="max-h-[400px] overflow-auto" role="group" aria-label="Vendors">
          {visible.length === 0 ? (
            <p className="text-center text-muted-foreground">No vendors found</p>
          ) : (
            visible.map((vendor) => {
              const selected = selectedKeys.has(`vendor:${vendor.id}`);
              const recipient = recipientByKey('vendor', vendor.id);
              const trades = vendor.trades.join(', ');
              return (
                <div key={vendor.id}>
                  <CheckRow
                    selected={selected}
                    disabled={!vendor.email}
                    testId={`vendor-option-${vendor.id}`}
                    onClick={() =>
                      onToggle({
                        kind: 'vendor',
                        id: vendor.id,
                        name: vendor.name,
                        subtitle: trades || null,
                        primary: vendor.email ?? null,
                      })
                    }
                  >
                    <div className="font-semibold text-foreground">{vendor.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {vendor.email
                        ? `${trades ? `${trades} · ` : ''}${vendor.email}${recipient?.extraAddresses.length ? ' (primary)' : ''}`
                        : NO_EMAIL_REASON}
                    </div>
                  </CheckRow>
                  {recipient && <SendToSelect recipient={recipient} onChoose={onChooseAddress} />}
                </div>
              );
            })
          )}
        </div>
      )}
    </>
  );
}

interface MessageStepProps {
  recipients: ComposeRecipient[];
  subject: string;
  bodyText: string;
  isSending: boolean;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onEdit: () => void;
  onSend: () => void;
}

function MessageStep({
  recipients,
  subject,
  bodyText,
  isSending,
  onSubjectChange,
  onBodyChange,
  onEdit,
  onSend,
}: MessageStepProps): JSX.Element {
  const tenantCount = recipients.filter((r) => r.kind === 'tenant').length;
  const vendorCount = recipients.length - tenantCount;
  const summary = [
    tenantCount > 0 ? pluralize(tenantCount, 'tenant') : null,
    vendorCount > 0 ? pluralize(vendorCount, 'vendor') : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <div className="p-3 mb-4 rounded-md border border-border bg-muted/50" data-testid="compose-to-summary">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="text-xs font-semibold text-muted-foreground">To · {summary}</div>
            {recipients.map((r) => (
              <div key={keyOf(r)} className="flex flex-wrap items-center gap-2" data-testid={`to-${r.id}`}>
                <Badge variant={r.kind === 'vendor' ? 'info' : 'secondary'}>
                  {r.name}
                  {r.subtitle ? ` · ${r.subtitle}` : ''}
                </Badge>
                <span className="text-xs text-muted-foreground truncate">{r.address}</span>
                {r.address && r.address !== r.primary && (
                  <Badge variant="secondary" data-testid={`not-primary-${r.id}`}>
                    not primary
                  </Badge>
                )}
              </div>
            ))}
          </div>
          <Button variant="ghost" onClick={onEdit} data-testid="compose-edit-recipients">
            Edit
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Each person gets their own copy and cannot see who else you wrote to. Replies come back to one
        thread here.
      </p>

      <div className="mb-4">
        <label htmlFor="compose-subject" className="block mb-2 font-semibold text-sm text-foreground">
          Subject
        </label>
        <input
          id="compose-subject"
          type="text"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder="Email subject"
          className="w-full p-3 border border-input rounded-md text-base outline-none min-h-[44px] bg-background text-foreground"
          data-testid="subject-input"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="compose-body" className="block mb-2 font-semibold text-sm text-foreground">
          Message
        </label>
        <textarea
          id="compose-body"
          value={bodyText}
          onChange={(e) => onBodyChange(e.target.value)}
          placeholder="Type your message..."
          rows={10}
          className="w-full p-3 border border-input rounded-md text-base font-[inherit] resize-y outline-none bg-background text-foreground"
          data-testid="content-textarea"
        />
      </div>

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onEdit} data-testid="compose-back-button">
          Back
        </Button>
        <Button onClick={onSend} disabled={isSending} data-testid="send-button">
          {isSending ? 'Sending...' : `Send to ${pluralize(recipients.length, 'recipient')}`}
        </Button>
      </div>
    </>
  );
}
